import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import DataLoader from "../../../../components/ui/dataLoader/DataLoader";
import FormCreator from "../../../../components/Forms/FormCreator";
import Accordion from "../../../../components/Accordion/Accordion";
import Buttons from "../../../../components/ui/button/Buttons";
import CenterModel from "../../../../components/ui/centerModel/CenterModel";
import { useNavigate, useParams } from "react-router-dom";
import SwalAlert from "../../../../services/swalService/SwalService";
import { useAddEditRequirementAdditionalFieldMutation, useEditRequirmentInformationMutation, useLazyDeleteRequirementAdditionalFieldByIdQuery, useLazyGetAllDropdownForCRUDCategoryQuery, useLazyGetSGBPRequirementDetailsByIdQuery, } from "../../../../app/services/Crud/crudApi";
import { setAllDropDownField } from "../Utils/dropdownUtils";
import { AddRequirementData } from "../addRequirement/data/AddRequirement.data";
import { AdditionalFieldsData } from "../addRequirement/data/AdditionalFields.data";
import { decryptAESID } from "../../../../utils/Encryption";
import ToastService from "../../../../services/toastService/ToastService";

const EditRequirementForm = () => {
  const editRequirementRefs = useRef();
  const additionalFieldRef = useRef();
  const { id } = useParams();
  const editID = id ? decryptAESID(id, false) : 0;
  const { success, error, confirm } = SwalAlert();
  const navigate = useNavigate();
  // const [formData, setFormData] = useState(AddRequirementData);
  const [additionalFieldsForm, setAdditionalFieldsForm] = useState(AdditionalFieldsData);
  const [rows, setRows] = useState([]);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [getDropdowns, { data: dropdownData, isLoading: dropdownLoading }] = useLazyGetAllDropdownForCRUDCategoryQuery();
  const [getRequirementDetails, { data: requirementData, isLoading: requirementLoading }] = useLazyGetSGBPRequirementDetailsByIdQuery();
  const [editRequirement, { isLoading: editLoading, data: editData }] = useEditRequirmentInformationMutation();
  const [addEditRequirementAdditionalField, { isLoading: addEditLoading, data: addEditData }] = useAddEditRequirementAdditionalFieldMutation();
  const [deleteRequirementAdditionalField, { isLoading: deleteLoading, data: deleteData }] = useLazyDeleteRequirementAdditionalFieldByIdQuery();

  useEffect(() => {
    getDropdowns();
  }, []);

  useEffect(() => {
    if (!dropdownLoading && dropdownData) {
      setAllDropDownField(dropdownData);
    }
  }, [dropdownLoading, dropdownData]);

  useEffect(() => {
    if (!editLoading && editData) {
      if (editData?.hasError) {
        ToastService.error(editData?.errorMessage)
      } else {
        success("Requirement updated successfully.");
        navigate("/crudRequirement");
      }
    }
  }, [editLoading, editData]);

  useEffect(() => {
    if (editID) {
      getRequirementDetails(editID);
    }
  }, [editID]);

  useEffect(() => {
    if (!requirementLoading && requirementData) {
      // setFormData({...formData,
      //   initialState: {
      //     requirementDescription: requirementData?.requirementDescription,
      //     channel: requirementData?.channelId,
      //     requirementSection: requirementData?.requirementSectionId,
      //     requirementName: requirementData?.requirementName,
      //     documentToBeSubmitted: requirementData?.requirementToBeSubmitted,
      //   },
      // });
      const mappedRows = (requirementData?.additionalField || []).map((item, index) => ({
        id: item?.sgbpAdditionalFieldInputId,
        apiId: item?.sgbpAdditionalFieldInputId || null,
        label: item?.label,
        instructions: item?.addtionalFieldIntruction,
        inputType: item?.isTextBox ? "Text Box" : item?.isRating ? "L1-L4 Rating" : "Tick Rating",
        mandatory: item?.isMandatory ? "Yes" : "No",
      }));
      setRows(mappedRows);
    }
  }, [requirementLoading, requirementData]);

  const formData = useMemo(() => {
    if (!requirementData) return AddRequirementData;
    return {
      ...AddRequirementData,
      initialState: {
        channel: requirementData?.channelId,
        requirementSection: requirementData?.requirementSectionId,
        requirementName: requirementData?.requirementName,
        requirementDescription: requirementData?.requirementDescription,
        documentToBeSubmitted: requirementData?.requirementToBeSubmitted,
      },
    };
  }, [requirementData]);

  const handleAddField = () => {
    setEditId(null);

    setAdditionalFieldsForm({
      ...additionalFieldsForm,
      initialState: {
        label: "",
        inputType: "",
        mandatory: "",
        instructions: "",
      },
    });

    setShowAcceptModal(true);
  };

  const handleEdit = (id) => {
    const selected = rows.find((row) => row.id === id);
    setEditId(id);

    setAdditionalFieldsForm({
      ...additionalFieldsForm,
      initialState: {
        label: selected?.label,
        inputType: selected?.inputType,
        mandatory: selected?.mandatory,
        instructions: selected?.instructions,
      },
    });

    setShowAcceptModal(true);
  };

  const closeAcceptModal = () => {
    setShowAcceptModal(false);
    setEditId(null);

    setAdditionalFieldsForm({
      ...additionalFieldsForm,
      initialState: {
        label: "",
        inputType: "",
        mandatory: "",
        instructions: "",
      },
    });
  };

  const handleSubmitField = () => {
    const data = additionalFieldRef.current?.getFormData();

    if (!data || !data.label) {
      error("Please fill in all mandatory fields to continue.", "");
      return;
    }

    const selectedRow = rows?.find((row) => row.id === editId);

    const apiRequest = {
      RequirementId: editID,
      SGBPAdditionalFieldInputId: selectedRow?.apiId || 0,
      Label: data.label,
      AdditionalFieldIntructions: data.instructions,
      IsTextBox: data.inputType === "Text Box",
      IsRating: data.inputType === "L1-L4 Rating",
      IsTickRating: data.inputType === "Tick Rating",
      IsMandatory: data.mandatory === "Yes",
    };
    addEditRequirementAdditionalField(apiRequest);

    const formattedData = {
      label: data.label,
      inputType: data.inputType,
      mandatory: data.mandatory,
      instructions: data.instructions,
    };

    if (editId) {
      setRows((prev) =>
        prev.map((row) =>
          row.id === editId ? { ...row, ...formattedData } : row
        )
      );
      success("Record updated successfully.");
    } else {
      setRows((prev) => [
        ...prev,
        {
          ...formattedData,
          id: Date.now(),
          apiId: null,
        },
      ]);
      success("Record added successfully.");
    }
    closeAcceptModal();
  };

  const handleDelete = (id) => {
    confirm("Delete", "Are you sure you want to delete", "Yes", "Cancel")
      .then((value) => {
        if (value) {
          const deletedItem = rows.find((r) => r.id === id);

          if (deletedItem?.apiId) {
            deleteRequirementAdditionalField(deletedItem.apiId);
          }
          setRows((prev) => prev.filter((row) => row.id !== id));
          if (!deletedItem?.apiId) {
            success("Record deleted successfully.");
          }
        }
      });
  };

  const handleSubmit = () => {
    const data = editRequirementRefs?.current?.getFormData();

    if (!data) {
      error("Please fill in all mandatory fields to continue.", "");
      return;
    }

    const request = {
      SGBPRequirementId: editID,
      ChannelId: data?.channel?.value || data?.channel,
      RequirementSectionId: data?.requirementSection?.value || data?.requirementSection,
      RequirementName: data?.requirementName,
      RequirementDescription: data?.requirementDescription,
      DocumentToBeSubmitted: data?.documentToBeSubmitted,
      StatusId: 2
    };
    editRequirement(request)
  };

  const handleBack = () => {
    navigate("/crudRequirement");
  };

  return (
    <div className="add-requirement">
      {!requirementLoading ? <Suspense fallback={<DataLoader />}>
        <Accordion
          title="Requirement Information"
          className="mb-4"
          button={false}
          accordion={false}
        >
          <div className="row">
            {dropdownData && requirementData && (
              <FormCreator
                ref={editRequirementRefs}
                {...formData}
              />
            )}
          </div>

          <div className="additional-field-sec mt-5">
            <label>Additional Fields</label>
            <Buttons
              buttonText="Add Field"
              buttonTypeClassName="add-btn"
              onClick={handleAddField}
            />
          </div>

          <div className="additional-field-table">
            <div className="label-table">
              <table>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Input Type</th>
                    <th>Mandatory</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.length > 0 ? (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.label}</td>
                        <td>{row.inputType}</td>
                        <td>{row.mandatory}</td>
                        <td className="actions">
                          <span className="edit" onClick={() => handleEdit(row.id)}>
                            Edit
                          </span>
                          <span className="delete" onClick={() => handleDelete(row.id)}>
                            Delete
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ textAlign: "center" }}>
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Accordion>

        <div className="add-requirement-btn-wrapper mt-5">
          <Buttons
            buttonText="Back"
            buttonTypeClassName="btn btn-secondary mb-2"
            onClick={handleBack}
          />

          <div className="save-btn-wrapper">
            <Buttons
              buttonText="Approve"
              buttonTypeClassName="btn btn-secondary mb-2 disabled"
              disabled={true}
            />
            <Buttons
              buttonText="Save"
              buttonTypeClassName="add-btn"
              onClick={handleSubmit}
            />
          </div>
        </div>

        {showAcceptModal && (
          <CenterModel
            show={showAcceptModal}
            modelTitle={"Additional Fields"}
            handleClose={closeAcceptModal}
            modelWidth={"additional-field-modal"}
          >
            <FormCreator ref={additionalFieldRef} {...additionalFieldsForm} />

            <div className="btn-center-wrapper">
              <Buttons
                buttonText="Submit"
                buttonTypeClassName="add-btn"
                onClick={handleSubmitField}
              />
            </div>
          </CenterModel>
        )}
      </Suspense> : <DataLoader />}
    </div>
  );
};

export default EditRequirementForm;